import { t } from "@lingui/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { useState } from "react";
import type { SharedValue } from "react-native-reanimated";
import { useFragment } from "react-relay";

import { useFacilityLessonNoun } from "@/features/Facility";
import {
  LanguageSelectContainer,
  useAppliedLanguage,
} from "@/features/LanguagePreference";
import { ScreenHeader } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenHeader";
import { Button } from "@whoosh/design-system/src/components/controls";
import { ArrowLeftIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { Box } from "@whoosh/design-system/src/components/primitive";
import { MobileHeader } from "@whoosh/design-system/src/patterns/patronApp/MobileHeader/MobileHeader";
import { MobileNavigationBar } from "@whoosh/design-system/src/patterns/patronApp/MobileNavigationBar/MobileNavigationBar";
import { getThumbnail } from "@whoosh/utilities/src/Cloudinary";

import { FacilityLogo } from "../../../../../../features/Session/components/FacilityLogo";
import { ClubStackNavigatorParamList } from "../../../../../../navigators/ClubNavigator";
import { HomeTabsNavigatorParamList } from "../../../../../../navigators/HomeTabsNavigator/types";
import { useFlags } from "../../../../../../utils/launchDarkly";
import { LessonsFiltersModal } from "./LessonsFiltersModal";
import { LessonsHeaderFragment_facility$key } from "./__generated__/LessonsHeaderFragment_facility.graphql";
import { LessonsHeaderFragment_session$key } from "./__generated__/LessonsHeaderFragment_session.graphql";
import { useGetLessonAgendaFilterBar } from "./useGetLessonAgendaFilterBar";

type Props = {
  facilityRef: LessonsHeaderFragment_facility$key | null;
  sessionRef: LessonsHeaderFragment_session$key | null;
  scrolled: boolean;
  scrollY?: SharedValue<number>;
};

export const LessonsHeader = ({
  facilityRef,
  scrolled,
  sessionRef,
  scrollY,
}: Props) => {
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();

  const homeNavigation =
    useNavigation<NavigationProp<HomeTabsNavigatorParamList>>();
  const isMobile = useResponsiveProp({ base: 1, lg: 0 });

  const session = useFragment(LessonsHeaderSessionFragmentString, sessionRef);
  const isSignedIn = !!session?.currentClientProfile?.member?.id;

  const facility = useFragment(LessonsHeaderFragmentString, facilityRef);
  const clubName = facility?.club?.name || "";

  const { singular: lessonNoun, plural: lessonsNoun } = useFacilityLessonNoun(
    facility ?? null
  );

  const { newTopbar } = useFlags();
  const appliedLanguage = useAppliedLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const bookLessonTitle =
    appliedLanguage === "en" && /^[aeiou]/i.test(lessonNoun.trim())
      ? t`Book an ${lessonNoun}`
      : t`Book a ${lessonNoun}`;

  const handleBackPress = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      homeNavigation.navigate("MainHome");
    }
  };

  const filterBarValues = useGetLessonAgendaFilterBar(facility ?? null);

  return (
    <>
      <Box pt={{ base: 0, lg: 4 }}>
        {isMobile ? (
          <MobileHeader
            customNavigationBar={
              newTopbar ? null : (
                <MobileNavigationBar
                  leftAction={isSignedIn ? "Back" : null}
                  onLeftActionClick={isSignedIn ? handleBackPress : undefined}
                  title={clubName}
                  subTitle={bookLessonTitle}
                  rightElement={
                    <LanguageSelectContainer
                      syncToUserProfile={isSignedIn}
                      testID="legacy-navigation-language-select"
                    />
                  }
                  clubLogoImageUrl={
                    getThumbnail(
                      facility?.club?.logoImage?.publicId ?? "",
                      40,
                      40
                    )?.toURL() ?? ""
                  }
                />
              )
            }
            navigationBarVariant="default"
            filtersBar={{
              title: {
                leadingText: t`Find`,
                boldText: lessonsNoun,
              },
              values: filterBarValues,
              onPress: () => {
                setIsModalOpen(true);
              },
            }}
            scrollY={scrollY}
          />
        ) : (
          <ScreenHeader
            scrolled={scrolled}
            title={bookLessonTitle}
            subHeading={clubName}
            variant="REGULAR"
            isRounded
            titleRightElement={
              <FacilityLogo
                clubRef={facility?.club ?? null}
                imageProps={{
                  style: { height: 56, width: 56, borderRadius: 24 },
                }}
              />
            }
            leftItem={
              isSignedIn ? (
                <Button
                  accessibilityLabel={t`Back button`}
                  variant="secondary"
                  leftIcon={<ArrowLeftIcon />}
                  onPress={handleBackPress}
                  testID="mobile-header-back-button"
                />
              ) : null
            }
          />
        )}
      </Box>
      {isModalOpen ? (
        <LessonsFiltersModal
          isOpen={isModalOpen}
          facilityRef={facility ?? null}
          sessionRef={session ?? null}
          onClose={() => {
            setIsModalOpen(false);
          }}
        />
      ) : null}
    </>
  );
};

const LessonsHeaderFragmentString = graphql`
  fragment LessonsHeaderFragment_facility on Facility {
    ...useFacilityLessonNoun_facility
    ...useGetLessonAgendaFilterBar_facility
    ...DurationBasedFiltersModalFragment_facility
    ...LessonsFiltersModalFragment_facility
    club {
      name
      logoImage {
        # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
        publicId: renderingKey
      }
      ...FacilityLogoFragment_club
    }
  }
`;

const LessonsHeaderSessionFragmentString = graphql`
  fragment LessonsHeaderFragment_session on Session {
    ...LessonsFiltersModalFragment_session
    currentClientProfile {
      member {
        ... on Customer {
          id
        }
        ... on Member {
          id
        }
      }
    }
  }
`;
